import {
  Button,
  FormControl,
  Heading,
  Input,
  Stack,
  useColorModeValue,
  Image,
  VStack,
  HStack,
  FormLabel,
  Text,
  Card,
  CardBody,
  Spinner,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import {
  useAccount,
  usePrepareSendTransaction,
  useSendTransaction,
  useTransaction,
  useWaitForTransaction,
} from 'wagmi'
import InscriptionSvg from '../assets/inscription.svg'
import { FileUpload } from '../components'
import { hexToString, shortAddress } from '../utils/minorUtils'

export default function Inscription() {
  const { isDisconnected } = useAccount()
  const [to, setTo] = useState('')
  const [imageHex, setImageHex] = useState('')
  const [trxHash, setTrxHash] = useState('')
  const [inscription, setInscription] = useState({ sender: '', receiver: '', img: '' })
  const {
    config,
    isError: isPrepareError,
    error: prepareError,
  } = usePrepareSendTransaction({
    request: {
      to: to as `0x{string}`,
      value: 0,
      data: `${imageHex}`,
    },
  })
  const { data, sendTransaction, isError, error } = useSendTransaction(config)

  const { isLoading } = useWaitForTransaction({
    hash: data?.hash,
    onSuccess(data) {
      setTrxHash(data?.transactionHash)
    },
  })

  const {
    data: trxData,
    isLoading: isTrxDataLoading,
    isFetched,
  } = useTransaction({
    hash: trxHash as `0x${string}`,
    enabled: !!trxHash,
  })

  useEffect(() => {
    if (trxData) {
      let imgString = hexToString(trxData.data.toString())
      setInscription({ sender: trxData.from, receiver: trxData.to || '', img: imgString })
    }
  }, [trxData])

  return (
    <VStack
      minH={{ base: '70vh', md: '67vh', lg: '76vh' }}
      wrap={'wrap'}
      align={'center'}
      justify={'center'}
    >
      <HStack
        minH={{ base: '70vh', md: '67vh', lg: '76vh' }}
        wrap={'wrap'}
        align={'center'}
        justify={'center'}
      >
        <Image src={InscriptionSvg} boxSize={{ base: '170px', md: 'sm' }}></Image>
        <Stack
          spacing={4}
          bg={useColorModeValue('white', 'gray.700')}
          rounded={'xl'}
          boxShadow={'lg'}
          px={{ base: '6', md: '20' }}
          py={{ base: '6', md: '12' }}
          my={{ base: '6', md: '12' }}
        >
          <Heading fontSize={{ base: '2xl', md: '4xl' }} color={'brand.500'} alignSelf={'center'}>
            Inscriptions 🙉
          </Heading>
          <FileUpload setImageHex={setImageHex} />
          <FormControl id="recipient">
            <FormLabel>Recipient Address</FormLabel>
            <Input
              placeholder="0xA0Cf…251e"
              _placeholder={{ color: 'gray.500' }}
              type="text"
              onChange={(e) => setTo(e.target.value)}
              value={to}
            />
          </FormControl>
          <Stack spacing={6}>
            <Button
              bg={'brand.500'}
              color={'white'}
              _disabled={{
                bg: 'gray.600',
                cursor: 'not-allowed',
              }}
              _hover={{
                bg: 'brand.600',
              }}
              onClick={(e) => {
                e.preventDefault()
                sendTransaction?.()
              }}
              isDisabled={isDisconnected || isLoading}
            >
              {isLoading && <Spinner size="sm" thickness="2px" color="white" mx={'3px'} />}
              {isDisconnected ? 'Wallet Not Connected' : isLoading ? 'Minting' : 'Mint Inscription'}
            </Button>
          </Stack>

          {isTrxDataLoading && (
            <Text alignSelf={'center'} mt={'10px'}>
              Loading Inscription...
            </Text>
          )}
          {isFetched && (
            <Card direction={{ base: 'column', sm: 'row' }} overflow="hidden" variant="outline">
              <Image
                objectFit="contain"
                maxW={{ base: '80%', md: '150px' }}
                src={inscription.img}
                alt="Recent Inscription"
              />
              <Stack>
                <CardBody>
                  <Heading size="md">New Inscription</Heading>
                  <Text pt="6">From: {shortAddress(inscription.sender)}</Text>
                  <Text py="2">To: {shortAddress(inscription.receiver)}</Text>
                  <Button variant="solid" colorScheme="teal" mt="2">
                    View Transaction
                  </Button>
                </CardBody>
              </Stack>
            </Card>
          )}
          {(isPrepareError || isError) && (
            <div>Error: {(prepareError || error)?.message.substring(7, 57)}</div>
          )}
        </Stack>
      </HStack>
    </VStack>
  )
}
